import { anzeigePfad } from './auswahl';
import type { AufgeloesterKatalog } from './typen';

/**
 * Katalogangaben in der Mängelliste: Piktogramm vor dem Titel, Tooltip
 * „Absturz › Seitenschutz“ und der Filter „Thema“.
 *
 * Struktureller Typ statt Pick<InspectionItem, …>: die Felder kommen mit
 * Paket E an InspectionItem; so hängt die Liste nicht vom Stand von
 * src/types.ts ab.
 */
export interface KatalogFelderAmMangel {
    katalogKey?: string | null;
    katalogKategorie?: string | null;
}

export interface KatalogAnzeige {
    pikto: string;
    pfad: string[];
    /** „Absturz › Seitenschutz“ */
    tooltip: string;
}

const wert = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/**
 * Knoten auflösbar → dessen Piktogramm/Pfad; sonst die Kategorie. Unbekannte
 * Kategorie → Piktogramm 'allgemein', Name = ID. Beides leer → null.
 */
export function katalogAnzeige(item: KatalogFelderAmMangel, katalog: AufgeloesterKatalog): KatalogAnzeige | null {
    if (!item) return null;
    const key = wert(item.katalogKey);
    const kategorieId = wert(item.katalogKategorie);
    let pikto = 'allgemein';
    let pfad: string[] = [];
    const knoten = key ? katalog.knoten.get(key) : undefined;
    if (knoten) {
        pikto = knoten.pikto;
        pfad = anzeigePfad(knoten);
    } else if (kategorieId) {
        const kategorie = katalog.knoten.get(kategorieId);
        pikto = kategorie?.pikto ?? 'allgemein';
        pfad = [kategorie?.name ?? kategorieId];
    } else {
        return null;
    }
    return { pikto, pfad, tooltip: pfad.join(' › ') };
}

/**
 * Themen (Kategorien) der Liste mit Anzahl; Reihenfolge wie in der Torte,
 * unbekannte Kategorien dahinter nach Namen.
 */
export function themenDerListe(
    items: readonly KatalogFelderAmMangel[],
    katalog: AufgeloesterKatalog,
): { id: string; name: string; anzahl: number }[] {
    const anzahl = new Map<string, number>();
    for (const item of items ?? []) {
        const id = wert(item?.katalogKategorie);
        if (id) anzahl.set(id, (anzahl.get(id) ?? 0) + 1);
    }
    const reihenfolge = katalog.kinder.get('') ?? [];
    const pos = new Map(reihenfolge.map((id, i) => [id, i] as const));
    return [...anzahl.entries()]
        .map(([id, n]) => ({ id, name: katalog.knoten.get(id)?.name ?? id, anzahl: n }))
        .sort((a, b) => {
            const pa = pos.get(a.id);
            const pb = pos.get(b.id);
            if (pa !== undefined && pb !== undefined) return pa - pb;
            if (pa !== undefined) return -1;
            if (pb !== undefined) return 1;
            return a.name.localeCompare(b.name, 'de') || a.id.localeCompare(b.id);
        });
}

/** Filter „Thema“; null = alle (dieselbe Liste zurück). */
export function nachThemaFiltern<T extends Pick<KatalogFelderAmMangel, 'katalogKategorie'>>(items: T[], themaId: string | null): T[] {
    if (!themaId) return items;
    return items.filter((i) => wert(i?.katalogKategorie) === themaId);
}
